import { OPERATOR } from 'legal';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { openTelegram } from '@/shared/lib/telegram';
import { Button } from './Button';
import { Illustration } from './Illustration';

/*
 * Два вида ошибки загрузки — по тому, сколько запросов на экране:
 *
 * - `ErrorBlock` — страница собрана из нескольких независимых блоков
 *   («Статистика»: «Прогресс» + «Динамика»). Упал один — на его месте карточка
 *   «Что-то пошло не так» + «Обновить», соседние блоки живут дальше.
 * - `ErrorScreen` — экран держится на одном запросе. Тогда вместо контента
 *   полноэкранная заглушка с картинкой.
 *
 * Текст самой ошибки (`toUserMessage(error)`, «Load failed», путь запроса)
 * пользователю не показываем — это лог, а не подсказка, что делать. `message`
 * остаётся только для смысловых состояний («разговор закрыт», «не найдено»).
 */

interface ErrorBlockProps {
  onRetry?: () => void;
  className?: string;
}

export function ErrorBlock({ onRetry, className }: ErrorBlockProps) {
  const { t } = useTranslation('StatusView');

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-4 rounded-3xl border border-hairline bg-glass px-6 py-8 text-center text-ink shadow-lg',
        className,
      )}
    >
      <p className="text-lg font-semibold">{t('somethingWrong')}</p>
      {onRetry && (
        <Button variant="ghost" onClick={onRetry} className="min-w-28">
          {t('retry')}
        </Button>
      )}
    </div>
  );
}

interface ErrorScreenProps {
  title?: string;
  /** Только для смысловых состояний («не найдено»), не для текста ошибки. */
  message?: string;
  onRetry?: () => void;
  /** Подпись действия: не всякую ошибку имеет смысл «обновлять». */
  retryLabel?: string;
  /**
   * Картинка «выдернутый провод» — про сбой загрузки. У «не найдено» и
   * «разговор закрыт» сети никто не ронял, там её выключают.
   */
  illustration?: boolean;
  /**
   * Второе действие под основным, текстовой ссылкой. По умолчанию у сбоя
   * («Что-то пошло не так», без своего `title`) — «Написать в поддержку»;
   * у смысловых экранов («не найдено») его нет. `null` — убрать совсем.
   */
  secondaryAction?: { label: string; onClick: () => void } | null;
  className?: string;
}

export function ErrorScreen({
  title,
  message,
  onRetry,
  retryLabel,
  illustration = true,
  secondaryAction,
  className,
}: ErrorScreenProps) {
  const { t } = useTranslation('StatusView');
  const secondary =
    secondaryAction === undefined && title == null
      ? { label: t('writeSupport'), onClick: () => openTelegram(OPERATOR.contact.href) }
      : secondaryAction;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center text-ink',
        className,
      )}
    >
      {illustration && (
        <Illustration
          src="/illustrations/unplugged.webp"
          className="mb-3 w-full max-w-60"
          fallback={<UnpluggedIllustration className="mb-3 w-full max-w-60" />}
        />
      )}
      <p className="text-xl font-semibold">{title ?? t('somethingWrong')}</p>
      {message && <p className="max-w-xs text-sm leading-relaxed text-ink-muted">{message}</p>}
      {onRetry && (
        <Button variant="ghost" onClick={onRetry} className="mt-2 min-w-32">
          {retryLabel ?? t('retry')}
        </Button>
      )}
      {secondary && (
        <Button variant="quiet" onClick={secondary.onClick} className="mt-2">
          {secondary.label}
        </Button>
      )}
    </div>
  );
}

/** Розетка, выдернутая вилка и роутер с погасшими огоньками. Цвета — токены темы. */
function UnpluggedIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 240 160" className={className} aria-hidden="true" fill="none">
      <circle
        cx="120"
        cy="84"
        r="66"
        fill="color-mix(in oklab, var(--color-signal-blue) 7%, transparent)"
      />

      <g fill="var(--color-glass)" stroke="var(--color-hairline)" strokeWidth="2">
        <path d="M30 40a9 9 0 0 1 16-6 11 11 0 0 1 20 5 7 7 0 0 1-1 13H33a7 7 0 0 1-3-12z" />
        <path d="M176 26a8 8 0 0 1 14-5 10 10 0 0 1 18 5 6 6 0 0 1-1 12h-29a6 6 0 0 1-2-12z" />
      </g>

      <ellipse cx="120" cy="138" rx="98" ry="5" fill="var(--color-hairline)" opacity="0.6" />

      {/* Розетка */}
      <rect
        x="20"
        y="76"
        width="36"
        height="42"
        rx="9"
        fill="var(--color-glass)"
        stroke="var(--color-hairline)"
        strokeWidth="2"
      />
      <rect x="31" y="89" width="4" height="14" rx="2" fill="var(--color-ink-muted)" />
      <rect x="41" y="89" width="4" height="14" rx="2" fill="var(--color-ink-muted)" />

      {/* Искры в зазоре */}
      <g stroke="var(--color-signal-blue)" strokeWidth="2.5" strokeLinecap="round">
        <path d="M62 84l4 5" />
        <path d="M60 97h6" />
        <path d="M62 110l4-5" />
      </g>

      {/* Провод от вилки к роутеру */}
      <path
        d="M100 97c20 0 8 38 40 36 20-1 26-10 32-16"
        stroke="var(--color-ink)"
        strokeWidth="3.5"
        strokeLinecap="round"
      />

      {/* Вилка */}
      <rect x="70" y="90" width="10" height="4" rx="2" fill="var(--color-ink-muted)" />
      <rect x="70" y="100" width="10" height="4" rx="2" fill="var(--color-ink-muted)" />
      <rect x="78" y="82" width="24" height="30" rx="7" fill="var(--color-ink)" />

      {/* Роутер */}
      <g stroke="var(--color-ink-muted)" strokeWidth="3" strokeLinecap="round">
        <path d="M172 100l-5-30" />
        <path d="M214 100l5-30" />
      </g>
      <rect
        x="160"
        y="99"
        width="66"
        height="24"
        rx="7"
        fill="var(--color-glass)"
        stroke="var(--color-hairline)"
        strokeWidth="2"
      />
      <circle cx="173" cy="111" r="3" fill="var(--color-hairline)" />
      <circle cx="183" cy="111" r="3" fill="var(--color-hairline)" />
      <rect x="194" y="108" width="22" height="6" rx="3" fill="var(--color-hairline)" />
    </svg>
  );
}

/** Заглушка на время загрузки чанка страницы или данных. */
export function LoadingView({ label }: { label?: string }) {
  const { t } = useTranslation('StatusView');

  return (
    <div
      className="flex h-full min-h-40 items-center justify-center"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{label ?? t('loading')}</span>
      <span
        aria-hidden="true"
        className="size-7 animate-spin rounded-full border-2 border-hairline border-t-signal-blue"
      />
    </div>
  );
}
